import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Shield, Loader2, Key, UserPlus, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSetup = () => {
  const [loading, setLoading] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  // First admin setup form
  const [setupForm, setSetupForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    setupKey: ''
  });

  // Invite code registration form
  const [inviteForm, setInviteForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    inviteCode: ''
  });

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/setup-check`);
      setSetupRequired(response.data.setup_required);
    } catch (error) {
      console.error('Failed to check setup status:', error);
      toast.error('Failed to check admin setup status');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    
    if (!setupForm.email || !setupForm.password || !setupForm.name || !setupForm.setupKey) {
      toast.error('Please fill in all fields');
      return;
    }

    if (setupForm.password !== setupForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (setupForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/admin/setup`, {
        email: setupForm.email,
        password: setupForm.password,
        name: setupForm.name,
        setup_key: setupForm.setupKey
      });

      // Store token and redirect
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      toast.success('Admin account created successfully!');
      navigate('/admin');
      window.location.reload(); // Refresh to update auth state
    } catch (error) {
      console.error('Setup failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create admin account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteForm.email || !inviteForm.password || !inviteForm.name || !inviteForm.inviteCode) {
      toast.error('Please fill in all fields');
      return;
    }

    if (inviteForm.password !== inviteForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (inviteForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/admin/register`, {
        email: inviteForm.email,
        password: inviteForm.password,
        name: inviteForm.name,
        invite_code: inviteForm.inviteCode
      });

      // Store token and redirect
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      toast.success('Admin account created successfully!');
      navigate('/admin');
      window.location.reload(); // Refresh to update auth state
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to register admin account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12" data-testid="admin-setup-page">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="font-serif text-2xl">Admin Setup</CardTitle>
          <CardDescription>
            {setupRequired 
              ? 'Set up your first admin account to manage the store'
              : 'Register as an admin using your invite code'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {setupRequired ? (
            // First Admin Setup Form
            <form onSubmit={handleSetupSubmit} className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>First-time setup:</strong> You&apos;re creating the first admin account. 
                  You&apos;ll need the setup key provided by your system administrator.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="setup-name">Full Name</Label>
                <Input
                  id="setup-name"
                  placeholder="Your name"
                  value={setupForm.name}
                  onChange={(e) => setSetupForm({ ...setupForm, name: e.target.value })}
                  disabled={submitting}
                  data-testid="setup-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="setup-email">Email</Label>
                <Input
                  id="setup-email"
                  type="email"
                  placeholder="admin@example.com"
                  value={setupForm.email}
                  onChange={(e) => setSetupForm({ ...setupForm, email: e.target.value })}
                  disabled={submitting}
                  data-testid="setup-email-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="setup-password">Password</Label>
                  <Input
                    id="setup-password"
                    type="password"
                    placeholder="Min 8 characters"
                    value={setupForm.password}
                    onChange={(e) => setSetupForm({ ...setupForm, password: e.target.value })}
                    disabled={submitting}
                    data-testid="setup-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-confirm">Confirm Password</Label>
                  <Input
                    id="setup-confirm"
                    type="password"
                    placeholder="Confirm password"
                    value={setupForm.confirmPassword}
                    onChange={(e) => setSetupForm({ ...setupForm, confirmPassword: e.target.value })}
                    disabled={submitting}
                    data-testid="setup-confirm-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="setup-key">Setup Key</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="setup-key"
                    type="password"
                    placeholder="Enter setup key"
                    value={setupForm.setupKey}
                    onChange={(e) => setSetupForm({ ...setupForm, setupKey: e.target.value })}
                    disabled={submitting}
                    className="pl-10"
                    data-testid="setup-key-input"
                  />
                </div>
              </div>
              
              <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="setup-submit">
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating Account...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="h-4 w-4" />
                    Create Admin Account
                  </span>
                )}
              </Button>
            </form>
          ) : (
            // Invite Code Registration Form
            <Tabs defaultValue="invite" className="space-y-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="invite">With Invite Code</TabsTrigger>
                <TabsTrigger value="login">Already Admin?</TabsTrigger>
              </TabsList>
              
              <TabsContent value="invite">
                <form onSubmit={handleInviteSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-name">Full Name</Label>
                    <Input
                      id="invite-name"
                      placeholder="Your name"
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                      disabled={submitting}
                      data-testid="invite-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="The email your invite was sent to"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      disabled={submitting}
                      data-testid="invite-email-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="invite-password">Password</Label>
                      <Input
                        id="invite-password"
                        type="password"
                        placeholder="Min 8 characters"
                        value={inviteForm.password}
                        onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
                        disabled={submitting}
                        data-testid="invite-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invite-confirm">Confirm Password</Label>
                      <Input
                        id="invite-confirm"
                        type="password"
                        placeholder="Confirm password"
                        value={inviteForm.confirmPassword}
                        onChange={(e) => setInviteForm({ ...inviteForm, confirmPassword: e.target.value })}
                        disabled={submitting}
                        data-testid="invite-confirm-input"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="invite-code">Invite Code</Label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="invite-code"
                        placeholder="Enter your invite code"
                        value={inviteForm.inviteCode}
                        onChange={(e) => setInviteForm({ ...inviteForm, inviteCode: e.target.value.toUpperCase() })}
                        disabled={submitting}
                        className="pl-10 uppercase"
                        data-testid="invite-code-input"
                      />
                    </div>
                  </div>
                  
                  <Button type="submit" className="w-full btn-primary" disabled={submitting} data-testid="invite-submit">
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Registering...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <UserPlus className="h-4 w-4" />
                        Register as Admin
                      </span>
                    )}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="login" className="text-center py-6">
                <p className="text-muted-foreground mb-4">
                  If you already have an admin account, go to the login page.
                </p>
                <Link to="/login">
                  <Button variant="outline">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Go to Login
                  </Button>
                </Link>
              </TabsContent>
            </Tabs>
          )}

          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
              <ArrowLeft className="h-3 w-3 inline mr-1" />
              Back to Store
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSetup;
